import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { api, ApiError } from '../api';
import { formatRelative } from '../format';
import type { AssistantConversation, AssistantItem, AssistantOverview, CurrentUser, LocationSummary } from '../types';

const TOOL_WORDS: Record<string, string> = {
  list_subaccounts: 'sub-accounts',
  subaccount_report: 'report figures',
  search_contacts: 'contact search',
  get_contact: 'contact details',
  get_contact_notes: 'notes',
  find_leads: 'lead list',
  get_conversation: 'conversation',
  list_pipelines: 'pipelines',
  list_opportunities: 'deals',
  list_calendars: 'calendars',
  list_appointments: 'appointments',
  get_free_slots: 'free slots',
  list_team: 'team',
  list_workflows: 'workflows',
  list_calls: 'calls',
  get_call_transcript: 'call transcript',
  search_ghl_contacts: 'HighLevel contacts',
  describe_database: 'database layout',
  query_database: 'database',
  find_deleted_contacts: 'deleted contacts',
};

const SUGGESTIONS = [
  'Which hot leads have not been called in the last 48 hours?',
  'How did this sub-account do in the last 30 days?',
  'Show the deals stuck in the same stage for 2+ weeks',
  "What's on the calendar for the next 7 days?",
];

/** The cells of a Markdown table row: `| a | b |` → ['a', 'b']. */
function cells(line: string): string[] {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());
}

/** Bold, inline code, bullets, tables and line breaks — the formatting replies use. */
function RichText({ text }: { text: string }) {
  const lines = text.split('\n');
  const blocks: ReactNode[] = [];
  let list: string[] = [];
  let table: string[][] = [];
  const inline = (line: string) =>
    line.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, index) =>
      part.startsWith('**') && part.endsWith('**') && part.length > 4 ? (
        <strong key={index}>{part.slice(2, -2)}</strong>
      ) : part.startsWith('`') && part.endsWith('`') && part.length > 2 ? (
        <code key={index}>{part.slice(1, -1)}</code>
      ) : (
        <Fragment key={index}>{part}</Fragment>
      ),
    );
  const flush = () => {
    if (list.length) {
      blocks.push(
        <ul key={`l${blocks.length}`}>
          {list.map((item, index) => (
            <li key={index}>{inline(item)}</li>
          ))}
        </ul>,
      );
      list = [];
    }
    if (table.length) {
      const [head, ...body] = table;
      blocks.push(
        <div className="table-wrap rich-table" key={`t${blocks.length}`}>
          <table>
            <thead>
              <tr>
                {head!.map((cell, index) => (
                  <th key={index}>{inline(cell)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {head!.map((_, index) => (
                    <td key={index}>{inline(row[index] ?? '')}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      table = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^\s*\|.*\|\s*$/.test(line)) {
      if (list.length) {
        const pending = table;
        table = [];
        flush();
        table = pending;
      }
      // The |---|---| line under the header carries no data.
      if (!/^\s*\|?[\s:|-]+\|?\s*$/.test(line) || !line.includes('-')) table.push(cells(line));
      continue;
    }
    const bullet = /^\s*(?:[-*•]|\d+[.)])\s+(.*)$/.exec(line);
    if (bullet) {
      if (table.length) flush();
      list.push(bullet[1]!);
      continue;
    }
    flush();
    if (!line.trim()) continue;
    const heading = /^#{1,4}\s+(.*)$/.exec(line);
    blocks.push(<p key={`p${blocks.length}`}>{heading ? <strong>{heading[1]}</strong> : inline(line)}</p>);
  }
  flush();
  return <div className="rich">{blocks}</div>;
}

export function Assistant({ locations, user }: { locations: LocationSummary[]; user: CurrentUser | null }) {
  const [overview, setOverview] = useState<AssistantOverview | null>(null);
  const [conversation, setConversation] = useState<AssistantConversation | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<string>('');
  const bottom = useRef<HTMLDivElement>(null);

  const refresh = useCallback(() => {
    api.assistant().then(setOverview).catch((cause) => setError(cause instanceof ApiError ? cause.message : String(cause)));
  }, []);
  useEffect(refresh, [refresh]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [conversation?.items.length, busy]);

  async function openConversation(id: string) {
    setError(null);
    try {
      const next = await api.conversation(id);
      setConversation(next);
      setLocationId(next.locationId ?? '');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause));
    }
  }

  function newChat() {
    setConversation(null);
    setText('');
    setError(null);
  }

  async function send(message = text) {
    const trimmed = message.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    let current = conversation;
    try {
      if (!current) current = await api.newConversation(locationId || null);
      setConversation({
        ...current,
        items: [...current.items, { kind: 'user', id: -Date.now(), text: trimmed, at: Date.now() }],
      });
      setText('');
      setConversation(await api.sendAssistantMessage(current.id, trimmed));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause));
      if (current) setConversation(await api.conversation(current.id).catch(() => current));
    } finally {
      setBusy(false);
      refresh();
    }
  }

  async function decide(actionId: string, decision: 'approve' | 'reject') {
    if (!conversation) return;
    setBusy(true);
    setError(null);
    try {
      setConversation(await api.decideAction(actionId, decision));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause));
      setConversation(await api.conversation(conversation.id).catch(() => conversation));
    } finally {
      setBusy(false);
      refresh();
    }
  }

  async function changeLocation(next: string) {
    setLocationId(next);
    if (conversation) {
      try {
        setConversation(await api.setConversationLocation(conversation.id, next || null));
      } catch (cause) {
        setError(cause instanceof ApiError ? cause.message : String(cause));
      }
    }
  }

  async function remove(id: string) {
    if (!window.confirm('Delete this conversation? Changes already made in HighLevel stay.')) return;
    await api.deleteConversation(id).catch(() => undefined);
    if (conversation?.id === id) newChat();
    refresh();
  }

  if (!overview) return error ? <div className="banner error">{error}</div> : <p className="empty">Loading…</p>;

  const pending = conversation?.pending ?? false;
  const canAct = overview.capabilities.some((capability) => capability.level === 'act');

  return (
    <div className="assistant">
      <aside className="assistant-list">
        <button type="button" className="btn btn-primary" onClick={newChat}>
          New chat
        </button>
        <div className="assistant-threads">
          {overview.conversations.map((item) => (
            <div key={item.id} className={`thread${conversation?.id === item.id ? ' current' : ''}`}>
              <button type="button" className="thread-open" onClick={() => openConversation(item.id)}>
                <span className="thread-title">{item.title ?? 'New chat'}</span>
                <span className="thread-meta">
                  {formatRelative(item.updatedAt)}
                  {item.pending ? ` · ${item.pending} to approve` : ''}
                </span>
              </button>
              <button type="button" className="icon-button" aria-label="Delete conversation" onClick={() => remove(item.id)}>
                ✕
              </button>
            </div>
          ))}
          {overview.conversations.length === 0 ? <p className="small muted">No conversations yet.</p> : null}
        </div>
      </aside>

      <section className="assistant-chat card">
        <div className="assistant-head">
          <div className="field">
            <label htmlFor="assistant-location">Sub-account</label>
            <select id="assistant-location" value={locationId} onChange={(event) => changeLocation(event.target.value)}>
              <option value="">Any — I’ll say which</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>
          <div className="capabilities" title="What the Assistant may do for you — set by an owner under Settings → Users">
            {overview.capabilities.map((capability) => (
              <span key={capability.area} className={`level-pill level-pill-${capability.level}`}>
                {capability.label}: {capability.level === 'act' ? 'act' : capability.level === 'view' ? 'view' : 'off'}
              </span>
            ))}
          </div>
        </div>

        {!overview.ready ? (
          <div className="ai-panel ai-empty">
            <span className="ai-spark" aria-hidden="true">✦</span>
            <span>
              The Assistant needs an AI key. {user?.canManageSettings ? 'Add one under Settings → AI.' : 'Ask an admin to add one under Settings → AI.'}
            </span>
          </div>
        ) : null}

        <div className="assistant-messages">
          {!conversation || conversation.items.length === 0 ? (
            <div className="assistant-empty">
              <div className="ai-headline">What can I help with?</div>
              <p className="small muted">
                Ask about leads, deals, appointments and performance across your sub-accounts
                {canAct ? ', or ask me to make changes — every change waits for your approval.' : '.'}
              </p>
              <div className="chips">
                {SUGGESTIONS.map((suggestion) => (
                  <button type="button" className="chip" key={suggestion} disabled={!overview.ready || busy} onClick={() => send(suggestion)}>
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            conversation.items.map((item) => <Item key={`${item.kind}-${item.id}`} item={item} busy={busy} onDecide={decide} />)
          )}
          {busy ? (
            <div className="bubble bubble-assistant thinking">
              <span className="dot-pulse" aria-hidden="true" /> Working…
            </div>
          ) : null}
          <div ref={bottom} />
        </div>

        {error ? <div className="inline-result bad">{error}</div> : null}
        {pending ? <div className="inline-result">Approve or reject the proposed changes above to continue.</div> : null}

        <form
          className="assistant-input"
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}
        >
          <textarea
            aria-label="Message"
            rows={2}
            placeholder={pending ? 'Decide on the proposed changes first' : 'Ask, or tell me what to do…'}
            value={text}
            disabled={!overview.ready || pending}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void send();
              }
            }}
          />
          <button type="submit" className="btn btn-primary" disabled={!overview.ready || busy || pending || !text.trim()}>
            Send
          </button>
        </form>
      </section>
    </div>
  );
}

function Item({ item, busy, onDecide }: { item: AssistantItem; busy: boolean; onDecide: (id: string, decision: 'approve' | 'reject') => void }) {
  if (item.kind === 'user') return <div className="bubble bubble-user">{item.text}</div>;
  if (item.kind === 'assistant') {
    return (
      <div className="bubble bubble-assistant">
        <RichText text={item.text} />
      </div>
    );
  }
  if (item.kind === 'lookup') {
    return <div className="lookup">Checked {[...new Set(item.tools.map((tool) => TOOL_WORDS[tool] ?? tool))].join(', ')}</div>;
  }
  return (
    <div className={`action-card action-${item.status}`}>
      <div className="action-head">
        <span className="action-label">
          {item.status === 'pending'
            ? 'Proposed change'
            : item.status === 'done'
              ? '✓ Done'
              : item.status === 'rejected'
                ? 'Declined'
                : '✕ Failed'}
        </span>
        {item.locationName ? <span className="small muted">{item.locationName}</span> : null}
      </div>
      <div className="action-summary">{item.summary}</div>
      {item.error ? <div className="cell-sub">{item.error}</div> : null}
      {item.status === 'pending' ? (
        <div className="row">
          <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => onDecide(item.id, 'approve')}>
            Approve
          </button>
          <button type="button" className="btn btn-sm" disabled={busy} onClick={() => onDecide(item.id, 'reject')}>
            Reject
          </button>
        </div>
      ) : null}
    </div>
  );
}
